import { IconEye, IconEyeOff, IconPlus, IconPencil } from '@tabler/icons-react';
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import B2BButton from '../../components/common/B2BButton';
import ProductGrid from '../ProductGrid';
import notify from '../../utils/Notification';
import { createB2BAPI } from '../../api/Interceptor';
import { BASE_URL } from '../../api/EndPoints';
import './Articles.css';
import { Button, Modal, PasswordInput, Stack, Tabs, Tooltip, Badge, Pagination } from '@mantine/core';
import Navigation from '../../components/customer/sidebar/Navigation';
import { useMediaQuery, useDebouncedValue } from '@mantine/hooks';

const Articles = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width: 767px)");
  const isTablet = useMediaQuery("(min-width: 768px) and (max-width: 991px)");

  const userId = JSON.parse(localStorage.getItem('user'))?.userId;
  const B2B_API = createB2BAPI();

  // States
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [isError, setIsError] = useState(false);

  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 5 });
  const [rowCount, setRowCount] = useState(0);
  const [pageCount, setPageCount] = useState(0);

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm] = useDebouncedValue(searchTerm, 500);
  const [activeTab, setActiveTab] = useState(location.state?.activeTab || location.state?.tabinfo || 'Draft');

  const [changePasswordOpened, setChangePasswordOpened] = useState(false);
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [swatchFilter, setSwatchFilter] = useState('');
  const [countAll, setCountAll] = useState(0);
  const [countRequested, setCountRequested] = useState(0);
  const [countNotRequested, setCountNotRequested] = useState(0);


  const reqInfo = useMemo(() => {
    return activeTab === 'Request Info' ? '&reqInfo=true' :
      activeTab === 'Un Approved' ? '&reqInfo=false' : '';
  }, [activeTab]);

  const swatchParam = useMemo(() => {
    if (swatchFilter === 'true') return '&swatchRequest=true&swatchSent=false';
    if (swatchFilter === 'false') return '&swatchRequest=false&swatchSent=false';
    return '';
  }, [swatchFilter]);

  // ==================== API Calls ====================

  const fetchFilterCounts = useCallback(async () => {
    try {
      const isRejected = activeTab === 'Rejected';
      const rejectedParam = isRejected ? '&rejected=true&swatchReceived=false' : '&rejected=false';
      const baseUrl = `draftProduct/products/search?page=0&size=1${rejectedParam}&searchTerm=${debouncedSearchTerm}${reqInfo}`;

      const [resAll, resReq, resNotReq] = await Promise.all([
        B2B_API.get(`${baseUrl}`).json(),
        B2B_API.get(`${baseUrl}&swatchRequest=true&swatchSent=false`).json(),
        B2B_API.get(`${baseUrl}&swatchRequest=false&swatchSent=false`).json()
      ]);

      setCountAll(resAll?.response?.totalElements || 0);
      setCountRequested(resReq?.response?.totalElements || 0);
      setCountNotRequested(resNotReq?.response?.totalElements || 0);
    } catch (error) {
      console.error('Error fetching filter counts:', error);
    }
  }, [debouncedSearchTerm, reqInfo, activeTab]);

  useEffect(() => {
    fetchFilterCounts();
  }, [fetchFilterCounts]);

  const fetchProfileVendorById = async () => {
    try {
      const res = await B2B_API.get(`vendor/${userId}`).json();
      if (res?.response?.firstTimeLogin === true) {
        setChangePasswordOpened(true);
      }
    } catch (error) {
      console.error('Error fetching vendor profile:', error);
    }
  };

  const fetchAllProducts = useCallback(async () => {
    try {
      setIsLoading(true);
      setIsError(false);

      const isRejected = activeTab === 'Rejected';
      const rejectedParam = isRejected ? '&rejected=true&swatchReceived=false' : '&rejected=false';

      const response = await B2B_API.get(
        `draftProduct/products/search?page=${pagination.pageIndex}&size=${pagination.pageSize}${rejectedParam}&searchTerm=${debouncedSearchTerm}${reqInfo}${swatchParam}`
      ).json();

      const data = response?.response || {};
      const rawProducts = data.content || [];
      const productsData = rawProducts.filter(item => !isRejected || item.swatchReceived !== true);

      setProducts(productsData);
      setRowCount(data.totalElements || 0);
      setPageCount(Math.ceil((data.totalElements || 0) / pagination.pageSize) || 1);
    } catch (error) {
      console.error('Error fetching products:', error);
      setIsError(true);
      setProducts([]);
      setRowCount(0);
      setPageCount(0);

      notify({
        id: 'fetch_products',
        error: true,
        success: false,
        title: error?.message || 'Failed to fetch products',
      });
    } finally {
      setIsLoading(false);
      setIsSearching(false);
    }
  }, [pagination.pageIndex, pagination.pageSize, debouncedSearchTerm, reqInfo, swatchParam, activeTab]);

  // ==================== Handlers ====================

  const handleTabClick = (tab) => {
    setActiveTab(tab);
    setPagination({ pageIndex: 0, pageSize: pagination.pageSize });
  };

  const handlePaginationChange = (newPagination) => {
    setPagination(newPagination);
  };

  const handleSearchChange = (value) => {
    setSearchTerm(value);
  };

  const editVarient = (varobj) => {
    navigate(
      `/createDraftProduct?id=${varobj?.draftProductId}&page=${pagination.pageIndex}&size=${pagination.pageSize}&search=${searchTerm}`,
      { state: { activeTab } }
    );
  };

  const handlePasswordChange = async () => {
    try {
      if (!oldPassword || !newPassword || !confirmPassword) {
        notify({ error: true, title: "Please fill in all fields." });
        return;
      }
      if (newPassword !== confirmPassword) {
        notify({ error: true, title: "New passwords do not match." });
        return;
      }

      const res = await B2B_API.post("vendor/changePassword", {
        json: {
          customerId: userId,
          currentPassword: oldPassword,
          newPassword,
        },
      }).json();

      if (res?.success || res?.response?.success) {
        notify({ success: true, title: "Password changed successfully" });
        setChangePasswordOpened(false);
      } else {
        notify({ error: true, title: res?.message || "Password change failed" });
      }
    } catch (error) {
      notify({ error: true, title: error?.message || "Something went wrong" });
    } finally {
      setOldPassword("");
      setNewPassword("");
      setConfirmPassword("");
    }
  };

  // ==================== Effects ====================

  // Initial Load
  useEffect(() => {
    fetchProfileVendorById();
  }, []);

  // Fetch products when pagination, search, or tab changes
  useEffect(() => {
    fetchAllProducts();
  }, [fetchAllProducts]);

  // Handle URL query params
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const page = queryParams.get('page');
    const size = queryParams.get('size');
    const search = queryParams.get('search');

    if (page && size) {
      setPagination({
        pageIndex: parseInt(page),
        pageSize: parseInt(size)
      });
      setSearchTerm(search || '');
    }
  }, [location.search]);

  const renderMobileCards = () => {
    if (isLoading || isSearching) return <div style={{ textAlign: "center", padding: "2rem" }}>Loading...</div>;
    if (!products || products.length === 0) return <div style={{ textAlign: "center", padding: "2rem", color: "#666" }}>No data found.</div>;

    return (
      <div className="articles-mobile-cards">
        {products.map((item, index) => {
          const timestamp = item.product?.createdDate || item.createdDate;
          const image = item.vendorImages?.[0];
          return (
            <div key={index} className="articles-card" style={{ padding: 0, overflow: 'hidden' }}>
              <div style={{ width: '100%', height: '220px', backgroundColor: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
                {image && image !== 'null' ? (
                  <img src={`${BASE_URL}${image.replace("/api", "")}?time=${Date?.now()}`} alt="Product" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : <span style={{ fontSize: '14px', color: '#999' }}>No Image</span>}

                {activeTab === 'Request Info' && item.swatchRequest && (
                  <div style={{ position: 'absolute', top: '10px', right: '10px' }}>
                    <Badge color={item.swatchRequest ? "green" : "red"} variant="filled" size="sm" radius="sm">
                      {item.swatchRequest ? "Swatch Requested" : ""}
                    </Badge>
                  </div>
                )}
              </div>

              <div className="articles-card-body" style={{ padding: '1.25rem', paddingBottom: '0.5rem' }}>
                <div className="articles-card-row">
                  <span className="articles-card-label">Created Date:</span>
                  <span className="articles-card-value">
                    {timestamp ? new Date(Number(timestamp)).toLocaleDateString('en-GB') : 'N/A'}
                  </span>
                </div>
                <div className="articles-card-row">
                  <span className="articles-card-label">Product Code:</span>
                  <span className="articles-card-value">{item.product?.vendorProductCode || item.vendorProductCode}</span>
                </div>
                <div className="articles-card-row">
                  <span className="articles-card-label">Product Name:</span>
                  <span className="articles-card-value">{item.vendorProductName}</span>
                </div>
              </div>

              <div className="articles-card-actions" style={{ padding: '1rem', marginTop: 0, borderTop: 'none', justifyContent: 'center' }}>
                <Button
                  size="sm"
                  variant="light"
                  color="#8BC53F"
                  fullWidth
                  leftSection={<IconPencil size={16} />}
                  onClick={() => editVarient(item)}
                >
                  Edit
                </Button>
              </div>
            </div>
          );
        })}
        {pageCount > 1 && (
          <div className="articles-pagination-mobile">
            <Pagination
              total={pageCount}
              value={pagination.pageIndex + 1}
              onChange={(page) => setPagination(p => ({ ...p, pageIndex: page - 1 }))}
              color="#8BC53F"
            />
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <div className="view-my-page router-view-container">
        <div className="route-view">
          {!isMobile && !isTablet && <Navigation />}

          <div>
            {activeTab === 'Draft' && (
              <div className="draft-re-unapp-create">
                <B2BButton
                  style={{ color: '#000' }}
                  name="New Development"
                  onClick={() => navigate('/VendorHome/createProduct')}
                  leftSection={<IconPlus size={15} />}
                  color="#c9e8b8"
                />
              </div>
            )}

            <Tabs value={activeTab} onChange={handleTabClick} style={{ width: '100%' }}>
              <Tabs.List
                styles={{
                  list: {
                    flexWrap: isMobile ? "wrap" : "nowrap",
                    overflowX: isMobile ? "hidden" : "auto",
                    whiteSpace: isMobile ? "normal" : "nowrap",
                    scrollbarWidth: 'none',
                    justifyContent: isMobile ? "space-between" : "flex-start",
                  },
                }}
              >
                <Tabs.Tab value="Draft" style={{ fontSize: isMobile ? '12px' : 'inherit', padding: isMobile ? '8px 4px' : '12px 16px', flex: isMobile ? '1' : 'none' }}>Product (draft)</Tabs.Tab>
                <Tabs.Tab value="Request Info" style={{ fontSize: isMobile ? '12px' : 'inherit', padding: isMobile ? '8px 4px' : '12px 16px', flex: isMobile ? '1' : 'none' }}>Product (WIP)</Tabs.Tab>
                <Tabs.Tab value="Un Approved" style={{ fontSize: isMobile ? '12px' : 'inherit', padding: isMobile ? '8px 4px' : '12px 16px', flex: isMobile ? '1' : 'none' }}>Product (Pending Approval)</Tabs.Tab>
                <Tabs.Tab value="Rejected" style={{ fontSize: isMobile ? '12px' : 'inherit', padding: isMobile ? '8px 4px' : '12px 16px', flex: isMobile ? '1' : 'none' }}>Product (Rejected)</Tabs.Tab>
              </Tabs.List>
            </Tabs>

            {isMobile ? (
              renderMobileCards()
            ) : (
              <ProductGrid
                isLoading={isLoading || isSearching}
                isError={isError}
                data={products}
                editVariant={editVarient}
                pagination={pagination}
                pageCount={pageCount}
                rowCount={rowCount}
                activeTab={activeTab}
                manualPagination={true}
                searchTerm={searchTerm}
                onPaginationChange={handlePaginationChange}
                handleSearchChange={handleSearchChange}
                swatchFilter={swatchFilter}
                setSwatchFilter={setSwatchFilter}
                countAll={countAll}
                countRequested={countRequested}
                countNotRequested={countNotRequested}
              />
            )}
          </div>
        </div>
      </div>

      {/* Change Password Modal */}
      <Modal
        opened={changePasswordOpened}
        title="Change Password"
        centered
        overlayProps={{ backgroundOpacity: 0.3, blur: 2 }}
        closeOnClickOutside={false}
        closeOnEscape={false}
        withCloseButton={false}
      >
        <Stack>
          <PasswordInput
            label="Old Password"
            value={oldPassword}
            onChange={(e) => setOldPassword(e.currentTarget.value)}
            visibilityToggleIcon={({ reveal }) => reveal ? <IconEyeOff size={16} /> : <IconEye size={16} />}
            required
          />
          <PasswordInput
            label="New Password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.currentTarget.value)}
            visibilityToggleIcon={({ reveal }) => reveal ? <IconEyeOff size={16} /> : <IconEye size={16} />}
            required
          />
          <PasswordInput
            label="Confirm New Password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.currentTarget.value)}
            visibilityToggleIcon={({ reveal }) => reveal ? <IconEyeOff size={16} /> : <IconEye size={16} />}
            required
          />

          <Button
            style={{ backgroundColor: "#8BC53F", color: "white", marginTop: 15 }}
            onClick={handlePasswordChange}
            fullWidth
          >
            Change Password
          </Button>

          <div style={{ color: "red", fontSize: "12px", textAlign: "center", marginTop: 8 }}>
            <span style={{ color: "grey" }}>NOTE:</span> Cannot close without changing password
          </div>
        </Stack>
      </Modal>
    </>
  );
};

export default Articles;